import {
    toDateString,
    toTimeString,
    laterThanTwoHoursFromNow
} from '@/../vue/online-booking/utils/dates';
import localDateTime from '@/../vue/online-booking/mixins/local-date-time';

//  ---- Filters for handling appointment choice paths. -----
// Mixin expects the following data to be available:
// - preferredDate format '30/2/22'
// - preferredTime format '10.00am'
// - appointments (raw array from API)
// - practitioners (raw array from API)
// - practitionerId (chosen practitionerId)

export default {
    mixins: [localDateTime], // Nested mixins.. Not great
    computed: {
        // ---- APPOINTMENTS -----

        // Returns all appointments with preferred practitioner on preffered date and time (where present)
        filteredAppointments() {
            const byPractitioner = this.appointmentsByPractitioner.map((apt) => apt.appointmentId);
            const byDate = this.appointmentsByDate.map((apt) => apt.appointmentId);
            const byTime = this.appointmentsByTime.map((apt) => apt.appointmentId);

            return this.appointments.filter((apt) => {
                const id = apt.appointmentId;
                return byPractitioner.includes(id) && byDate.includes(id) && byTime.includes(id);
            });
        },

        // Returns all appointments on the preferred date
        appointmentsByDateAndTime() {
            const onDate = this.appointmentsByDate.map((apt) => apt.appointmentId);
            const onTime = this.appointmentsByTime.map((apt) => apt.appointmentId);

            return this.appointments.filter(
                (apt) => onDate.includes(apt.appointmentId) && onTime.includes(apt.appointmentId)
            );
        },

        // Returns all appointments for the chosen practitioner
        appointmentsByPractitioner() {
            if (!this.practitionerId) return this.bookableAppointments;

            return this.bookableAppointments.filter(
                (apt) => apt.practitionerId === this.practitionerId
            );
        },

        // Returns all appointments on the preferred date
        appointmentsByDate() {
            if (!this.preferredDate) return this.bookableAppointments;

            return this.bookableAppointments.filter(
                (apt) => this.preferredDate === toDateString(apt.appointmentDate)
            );
        },

        // Returns all appointments on the preferred time
        appointmentsByTime() {
            if (!this.preferredTime) return this.bookableAppointments;

            return this.bookableAppointments.filter(
                (apt) => this.preferredTime === toTimeString(apt.appointmentTime)
            );
        },

        // Used dynamically in computed values to update appointments as minutes pass.
        bookableAppointments() {
            return this.appointments.filter((apt) => {
                const isPassed = laterThanTwoHoursFromNow(
                    apt.appointmentDateTime,
                    this.southAusDateTime
                );
                return !isPassed;
            });
        },

        // Returns all time slots on preferred date, format '10.00am'
        appointmentTimesOnDate() {
            return this.appointmentsByDate.reduce((acc, current) => {
                const aptTime = toTimeString(current.appointmentTime);
                return acc.includes(aptTime) ? acc : [...acc, aptTime];
            }, []);
        },

        // ---- PRACTITIONERS -----

        // Returns all practitioners who have appointments (in the timeframe requested from the API)
        practitionersWithAppointments() {
            const practitionerIds = this.bookableAppointments.map((apt) => apt.practitionerId);
            return this.practitioners.filter((pract) => practitionerIds.includes(pract.employeeId));
        },

        // Returns all practitioners who have appointments on the preferred date (where present)
        practitionersByDate() {
            const practitionerIds = this.appointmentsByDate.map((apt) => apt.practitionerId);
            return this.practitioners.filter((pract) => practitionerIds.includes(pract.employeeId));
        },

        // Returns all practitioners who have appointments on the preferred date and time (where present)
        practitionersByDateAndTime() {
            const practitionerIds = this.appointmentsByDateAndTime.map((apt) => apt.practitionerId);
            return this.practitioners.filter((pract) => practitionerIds.includes(pract.employeeId));
        },

        // Returns practitioners who have appointments on the preferred time and date
        filteredPractitioners() {
            const filtered = this.filteredAppointments.map((apt) => apt.practitionerId);
            return this.practitioners.filter((pract) => filtered.includes(pract.employeeId));
        }
    }
};
